import { useMedia } from 'react-use';
import { useToast } from '@hungryroot/components-toast';
import { useSaladShooter } from '@hungryroot/salad-shooter';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { type TriggerInteractionEventArgs } from '../RDPModal';
import { sendInteractionEvent } from '../utils/sendInteractionEvent';

export const useRDPModalAnalytics = () => {
  const saladShooter = useSaladShooter();
  const { toast } = useToast();
  const isDesktop = useMedia(BreakpointQueryLarge);

  const handleShare = (id: number, index: number | null) => {
    saladShooter.track([
      {
        kind: 'ui-action',
        action: {
          kind: 'shared',
          target: { kind: 'pairing', id },
          position: index,
          shared_from: 'rdp',
        },
      },
    ]);
    if (isDesktop) {
      toast('Link copied');
    }
  };

  const handleShareError = () => {
    if (isDesktop) {
      toast('Unable to copy link');
    }
  };

  const trackBookmark = (id: number, index: number | null) => {
    saladShooter.track([
      {
        kind: 'ui-action',
        action: {
          kind: 'saved',
          target: { kind: 'pairing', id },
          position: index,
          saved_from: 'rdp',
        },
      },
    ]);
  };

  const trackAddToCart = (id: number, index: number | null) => {
    saladShooter.track([
      {
        kind: 'ui-action',
        action: {
          kind: 'added-to-cart',
          target: { kind: 'pairing', id },
          position: index,
          added_from: 'rdp',
        },
      },
    ]);
  };

  const trackRemoveFromCart = (id: number, index: number | null) => {
    saladShooter.track([
      {
        kind: 'ui-action',
        action: {
          kind: 'removed-from-cart',
          target: { kind: 'pairing', id },
          position: index,
          removed_from: 'rdp',
        },
      },
    ]);
  };

  const trackEditIngredients = (id: number, index: number | null) => {
    saladShooter.track([
      {
        kind: 'ui-action',
        action: {
          kind: 'editing-ingredients',
          target: { kind: 'pairing', id },
          position: index,
        },
      },
    ]);
  };

  const trackNutritionToggle = (
    id: number,
    index: number | null,
    isExpanded: boolean
  ) => {
    saladShooter.track([
      {
        kind: 'ui-action',
        action: {
          kind: isExpanded ? 'expanded-nutrition' : 'collapsed-nutrition',
          target: { kind: 'pairing', id },
          position: index,
        },
      },
    ]);
  };

  const trackInteraction = (args: TriggerInteractionEventArgs) => {
    sendInteractionEvent(args);
  };

  return {
    handleShare,
    handleShareError,
    trackBookmark,
    trackAddToCart,
    trackRemoveFromCart,
    trackEditIngredients,
    trackNutritionToggle,
    trackInteraction,
  };
};

export type RDPModalAnalytics = ReturnType<typeof useRDPModalAnalytics>;
